import { ConfirmDialog } from '@revelio/ui';

export const UNSAVED_CHANGES_MESSAGE =
  'You have unsaved changes that will be lost. Do you want to leave this page?';

/**
 * In-app replacement for the `window.confirm` unsaved-changes prompt.
 *
 * A native confirm can be SUPPRESSED by the browser ("prevent this page from
 * creating additional dialogs" after repeated dialogs in one session), and a
 * suppressed dialog returns `false` instantly — indistinguishable from the
 * user clicking Cancel, so the leave-guard silently swallowed the click with
 * no feedback (the 2026-08-25 demo's "clicked the build link and nothing
 * happened"). An in-app dialog cannot be suppressed, makes Stay an explicit
 * user choice, and is drivable by headless automation (native confirms block
 * the assistant PW probes).
 *
 * Used by BOTH leave-guards: the router NavigationGuard (custom-dash pages)
 * and the assistant engine's redirect guard — one dialog, one copy.
 */
export function UnsavedChangesDialog({
  isOpen,
  onStay,
  onLeave,
}: {
  isOpen: boolean;
  /** Keep the user on the page (Cancel / overlay / Escape). */
  onStay: () => void;
  /** Discard the unsaved changes and continue the navigation. */
  onLeave: () => void;
}) {
  return (
    <ConfirmDialog
      open={isOpen}
      title="Unsaved changes"
      cancelLabel="Stay on page"
      confirmLabel="Leave page"
      destructive
      onCancel={onStay}
      onConfirm={onLeave}
      data-testid="unsaved-changes-dialog"
    >
      {UNSAVED_CHANGES_MESSAGE}
    </ConfirmDialog>
  );
}
